import React, { ReactNode, useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  FormControl,
  CircularProgress, // Import CircularProgress for the loader
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { FleetDashboardOverviewApi } from "../../../../API/Iot-Api/FleetDashboardOverview/FleetDashobardOverview";
import {
  DistanceInsightsReportResponse,
  EvCategoryListResponse,
  DistanceInsightsReportResponseDownload,
} from "../../../../@Types/DRO/IotApiResponse/FleetOverviewResponse";
import { CSVLink } from "react-csv";

const DistanceInsights = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [searchValInput, setSearchValInput] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState<any>();
  const [distanceInsightsReport, setDistanceInsightsReport] =
    useState<DistanceInsightsReportResponse | null>(null);
  const [distanceInsightsReportDownload, setDistanceInsightsReportDownload] =
    useState<DistanceInsightsReportResponseDownload | null>(null);
  const [evCategoryList, setEvCategoryList] =
    useState<EvCategoryListResponse | null>(null);
  const [month, setMonth] = useState<any>();
  const [evCategory, setEvCategory] = useState<any>();
  const [batteryType, setBatteryType] = useState<any>();
  const [downloadpage, setDownloadPage] = useState(1);
  const [downloadrowsPerPage, setDownloadRowsPerPage] = useState(50000);
  const [loading, setLoading] = useState(false); // New state for loading
  const [userRole, setUserRole] = useState<string | null>(null);
  useEffect(()=>{
    const role = localStorage.getItem("authority");
    setUserRole(role);
  },[])
  const pageRange = 5;
  const tableHeaderCellStyle = {
    fontWeight: 600,
    fontSize: "14px",
    color: "#455A64",
    font: "Rubik",
  };
  const tableBodyCellStyle = {
    fontWeight: 400,
    fontSize: "14px",
    color: "#A4A2A2",
    font: "Rubik",
  };

  const pageCount = Math.ceil(totalCount / rowsPerPage);
  const handlePageChange = (newPage: number) => {
    const newPageNumber = Math.max(1, Math.min(newPage + 1, pageCount));
    setPage(newPageNumber - 1);
  };

  const renderPageNumbers = () => {
    const pagesToShow = Math.min(pageCount, pageRange);
    const startPage = Math.max(
      0,
      Math.min(page - Math.floor(pageRange / 2), pageCount - pagesToShow)
    );

    return Array.from({ length: pagesToShow }, (_, index) => {
      const pageNumber = startPage + index + 1;
      return (
        <span
          key={index}
          onClick={() => handlePageChange(pageNumber - 1)}
          className={page === pageNumber - 1 ? "active" : "unactive"}
          style={{ marginLeft: "6px" }}
        >
          {pageNumber}
        </span>
      );
    });
  };

  const handleClear = () => {
    setBatteryType("");
  };

  const handleClearSearch = () => {
    setSearchValInput("");
  };

  useEffect(() => {
    const fetchDistanceInsightsReport = async () => {
      setLoading(true); // Set loading to true before the API call
      try {
        const data: DistanceInsightsReportResponse =
          await FleetDashboardOverviewApi.api.getDistanceInsightsReport(
            month,
            evCategory,
            batteryType,
            searchValInput,
            page + 1,
            rowsPerPage
          );

        setDistanceInsightsReport(data);
        setTotalCount(data.data.total_count);
      } catch (error) {
        console.error("Error fetching fleet summary", error);
      } finally {
        setLoading(false); // Set loading to false after the API call
      }
    };

    const fetchEvCategoryList = async () => {
      setLoading(true); // Set loading to true before the API call
      try {
        const data: EvCategoryListResponse =
          await FleetDashboardOverviewApi.api.getEvCategoryList();

        setEvCategoryList(data);
      } catch (error) {
        console.error("Error fetching fleet summary", error);
      } finally {
        setLoading(false); // Set loading to false after the API call
      }
    };

    fetchEvCategoryList();
    fetchDistanceInsightsReport();
  }, [month, evCategory, batteryType, searchValInput, page, rowsPerPage]);

  useEffect(() => {
    const fetchDistanceInsightsReportDownload = async () => {
      setLoading(true); // Set loading to true before the API call
      try {
        const data: DistanceInsightsReportResponseDownload =
          await FleetDashboardOverviewApi.api.getDistanceInsightsReportDownload(
            month,
            evCategory,
            batteryType,
            downloadpage,
            downloadrowsPerPage
          );

        setDistanceInsightsReportDownload(data);
      } catch (error) {
        console.error("Error fetching fleet summary", error);
      } finally {
        setLoading(false); // Set loading to false after the API call
      }
    };

    fetchDistanceInsightsReportDownload();
  }, [month, evCategory, batteryType, downloadpage, downloadrowsPerPage]);

  const distanceInsightsReportButton = [
    { label: "Vehicle No.", key: "qrCode" },
    { label: "EV Category", key: "fleetname" },
    { label: "Battery Model", key: "batteryType" },
    { label: "Week 1", key: "week_1" },
    { label: "Week 2", key: "week_2" },
    { label: "Week 3", key: "week_3" },
    { label: "Week 4", key: "week_4" },
  ];

  return (
    <div className="w-full flex bg-white min-h-80 shadow-lg p-3 rounded-xl">
      <div className="w-full">
        <Grid container spacing={5} className="w-full flex justify-between">
          <Grid item md={3}>
            <div className="flex ml-4">
              <h2 className="text-center ml-2 mx-1 mt-2 font-bold text-base text-black pb-2">
                Distance Report
              </h2>
            </div>
          </Grid>
          <Grid item md={9}>
            <div className="flex mr-4">
              <div className="flex w-full justify-end">
                <div>
                  <div
                    className="flex items-center bg-white border border-[#3b3b3b] border-solid rounded-full px-2 py-1 mr-2"
                    style={{ height: "40px" }}
                  >
                    <div>
                      <Typography className="font-inter text-[#727597] text-xs mr-2 font-normal">
                        Month:
                      </Typography>
                    </div>
                    <FormControl>
                      <select
                        id="month"
                        onChange={(e) => {
                          setMonth(e.target.value);
                        }}
                        value={month}
                      >
                        <option value="all" selected>
                          All
                        </option>
                        {[
                          "January",
                          "February",
                          "March",
                          "April",
                          "May",
                          "June",
                          "July",
                          "August",
                          "September",
                          "October",
                          "November",
                          "December",
                        ].map((month) => (
                          <option key={month} value={month}>
                            {month}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                  </div>
                </div>
                {(userRole === "ROLE_FLEET_SUPER_ADMIN" || 
    userRole === "ROLE_ADMIN" || 
    userRole === "ROLE_CO_ADMIN") && ( // Ensure this condition is checked
                <div>
                  <CSVLink
                    data={
                      searchValInput
                        ? distanceInsightsReport?.data
                            .distance_insight_report || [] // Use filtered data if filters are applied
                        : distanceInsightsReportDownload?.data
                            .distance_insight_report || [] // Default to the full download dataset
                    }
                    headers={distanceInsightsReportButton}
                    filename={`Distance_Insights_Report.csv`}
                    style={{ marginRight: "10px" }}
                  >
                    <Button
                      style={{
                        backgroundColor: "#06B681",
                        padding: "8px 20px",
                        color: "#fff",
                        borderRadius: "25px",
                        width: "100%",
                        textTransform: "none",
                        marginLeft: "10px",
                      }}
                    >
                      Download
                    </Button>
                  </CSVLink>
                </div>
                )}
              </div>
            </div>
          </Grid>
        </Grid>
        <hr className="my-4" />
        <div className="w-full flex bg-white min-h-80 shadow-lg p-3 rounded-xl">
          <div className="w-full">
            <Grid container spacing={5} className="w-full flex justify-between">
              <Grid item md={3}>
                <div className="flex ml-4">
                  <h2 className="text-center ml-2 mx-1 mt-2 font-bold text-base text-black pb-2">
                    Fleet Distance Report
                  </h2>
                </div>
              </Grid>
              <Grid item md={9}>
                <div className="flex mr-4">
                  <div className="flex w-full justify-end">
                    <div>
                      <div
                        className="flex items-center bg-white border border-[#3b3b3b] border-solid rounded-full px-2 py-1 mr-2"
                        style={{ height: "40px" }}
                      >
                        <div>
                          <Typography className="font-inter text-[#727597] text-xs mr-2 font-normal">
                            EV Category :
                          </Typography>
                        </div>
                        <FormControl>
                          <select
                            id="merchant"
                            onChange={(e) => {
                              setEvCategory(e.target.value);
                            }}
                            value={evCategory}
                          >
                            <option value="all">All</option>
                            {evCategoryList?.data?.fleet_category_list?.map(
                              (category: any) => (
                                <option key={category} value={category}>
                                  {category}
                                </option>
                              )
                            )}
                          </select>
                        </FormControl>
                      </div>
                    </div>
                    <div>
                      <div
                        className="flex items-center bg-white border border-[#3b3b3b] border-solid rounded-full px-2 py-1 mr-2"
                        style={{ height: "40px" }}
                      >
                        <div>
                          <Typography className="font-inter text-[#727597] text-xs mr-2 font-normal">
                            Battery Type :
                          </Typography>
                        </div>
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <select
                            id="battery"
                            value={batteryType}
                            onChange={(e) => setBatteryType(e.target.value)}
                            style={{
                              borderRadius: "5px",
                              paddingRight: "30px", // Space for the clear icon
                              appearance: "none",
                              WebkitAppearance: "none",
                              MozAppearance: "none",
                              textAlign: "center",
                            }}
                          >
                            <option value="all">All</option>
                            <option value="Swappable">Swappable</option>
                            <option value="Chargeable">Chargeable</option>
                          </select>

                          {batteryType !== "all" && (
                            <span
                              onClick={handleClear}
                              style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                color: "#999",
                                fontSize: "18px",
                                fontWeight: "bold",
                              }}
                            >
                              ✖
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mr-3">
                      <TextField
                        placeholder="Search  EB Number"
                        id="outlined-basic"
                        variant="outlined"
                        onChange={(search: any) =>
                          setSearchValInput(search.target.value)
                        }
                        value={searchValInput}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              onClick={handleClearSearch}
                            >
                              <ClearIcon style={{ cursor: "pointer" }} />
                            </InputAdornment>
                          ),
                          style: { height: "40px", borderRadius: "25px" },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Grid>
            </Grid>
            <hr className="my-4" />
            {loading ? ( // Conditionally render the loader
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        style={{ ...tableHeaderCellStyle, textAlign: "center" }}
                        sx={{ minWidth: 120 }}
                      >
                        Vehicle No.
                      </TableCell>
                      <TableCell
                        style={{ ...tableHeaderCellStyle, textAlign: "center" }}
                        sx={{ minWidth: 150 }}
                      >
                        EV Category
                      </TableCell>
                      <TableCell
                        style={{ ...tableHeaderCellStyle, textAlign: "center" }}
                        sx={{ minWidth: 110 }}
                      >
                        Battery Model
                      </TableCell>
                      <TableCell
                        style={{ ...tableHeaderCellStyle, textAlign: "center" }}
                        sx={{ minWidth: 110 }}
                      >
                        Week 1 (Km)
                      </TableCell>
                      <TableCell
                        style={{ ...tableHeaderCellStyle, textAlign: "center" }}
                        sx={{ minWidth: 100 }}
                      >
                        Week 2 (Km)
                      </TableCell>
                      <TableCell
                        style={{ ...tableHeaderCellStyle, textAlign: "center" }}
                        sx={{ minWidth: 100 }}
                      >
                        Week 3 (Km)
                      </TableCell>
                      <TableCell
                        style={{ ...tableHeaderCellStyle, textAlign: "center" }}
                        sx={{ minWidth: 100 }}
                      >
                        Week 4 (Km)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {distanceInsightsReport?.data?.distance_insight_report?.map(
                      (item, index) => (
                        <TableRow key={index}>
                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.qrCode}
                          </TableCell>
                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.fleetname}
                          </TableCell>
                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.batteryType}
                          </TableCell>
                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.week_1}
                          </TableCell>
                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.week_2}
                          </TableCell>
                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.week_3}
                          </TableCell>
                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.week_4}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <div className="pl-2 pt-4 pb-2 pr-16">
              <div
                className="pagination"
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <div>
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 0}
                    style={{
                      fontFamily: "Rubik",
                      fontSize: "20px",
                      marginRight: "6px",
                    }}
                  >
                    {"<"}
                  </button>
                  {renderPageNumbers()}
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= pageCount - 1}
                    style={{
                      fontFamily: "Rubik",
                      fontSize: "20px",
                      marginLeft: "6px",
                    }}
                  >
                    {">"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistanceInsights;
